import { useEffect, useMemo, useState } from "react";
import './PissarraAids.css'
import WatchersModal from "./watchers/WatchersModal";
import Pressable from "../other/Pressable";
import WatchersPreview from "./watchers/WatchersPreview";
import { isDesktop, isMobile } from "react-device-detect";
import { ProfilePic } from "./watchers/UserList";
import ChatButton from "./chat/ChatButton";
import moment from "moment";
import { fetchAPI } from "../../utils/utils";
import { isTecnica, isHeadTecnica } from "../../utils/permissions";
import { getEffectiveReferenceLabel, getEffectiveReferenceEmoji } from "../../utils/referenceLabel";

const getWatcherUserDevices = watcherInfo => (
    Array.isArray(watcherInfo) ?
        watcherInfo.reduce((acc, val) => (acc[val] = (acc[val] || 0) + 1, acc), {}) :
        {}
);

const areWatcherInfoEqual = (previous, next) => {
    if (previous === next) return true;
    if (!Array.isArray(previous) || !Array.isArray(next)) return false;
    if (previous.length !== next.length) return false;

    const previousDevices = getWatcherUserDevices(previous);
    const nextDevices = getWatcherUserDevices(next);
    const previousUsers = Object.keys(previousDevices);

    return previousUsers.length === Object.keys(nextDevices).length &&
        previousUsers.every(userId => previousDevices[userId] === nextDevices[userId]);
};

function PissarraAids(props) {
    const { castellersInfo, caixaSelected, posicions, socket, ajuntament, ajuntamentLabel, ajuntamentEmoji, pissarraConfig, ajuntamentModalClosed, rotationVal, extended, hideAjuntament, setHideAjuntament, setAjuntament, selectedEvent, selectedBundle, selectedVersio, readonly, hoveredUser, setHoveredUser, setSelectedBundle, setSelectedVersio, setInitializationOpacity, proves, userInfo, copilotSuggestion, acceptCopilotSuggestion, dismissCopilotSuggestion } = props;
    const position = ['top', 'right', 'bottom', 'left'][(ajuntament + Math.round(rotationVal/90))%4]
    const referenceLabel = getEffectiveReferenceLabel(ajuntamentLabel, pissarraConfig?.ajuntamentLabel)
    const referenceEmoji = getEffectiveReferenceEmoji(ajuntamentLabel, ajuntamentEmoji, pissarraConfig?.ajuntamentEmoji)

    const [closedWatcherPopup, setClosedWatcherPopup] = useState(true);
    const [watcherInfo, setWatcherInfo] = useState([])
    const [bundleNames, setBundleNames] = useState({});
    const [provaLabels, setProvaLabels] = useState({});
    const showPrivateLabels = readonly === false && isTecnica(userInfo);

    const userDevices = useMemo(() => getWatcherUserDevices(watcherInfo), [watcherInfo]);

    const selectedUser = caixaSelected !== -1 ? posicions?.caixes?.[caixaSelected] : null

    const suggestedUserIdRaw =
        caixaSelected !== -1 &&
        !selectedUser &&
        String(copilotSuggestion?.caixaId) === String(caixaSelected)
            ? parseInt(copilotSuggestion?.suggestedCastellerId, 10)
            : NaN;
    const suggestedUserId = Number.isNaN(suggestedUserIdRaw) ? null : suggestedUserIdRaw;

    const previewUser = hoveredUser || suggestedUserId;
    const isCopilotPreview = !hoveredUser && !!suggestedUserId;
    const previewUserName = castellersInfo?.[previewUser]?.mote || castellersInfo?.[previewUser]?.nom || ''

    const placedCount = Object.keys(posicions?.castellers || {}).length

    useEffect(() => {
        fetchAPI('/bundles', data => {
            if (!Array.isArray(data)) return;
            const names = data.reduce((acc, bundle) => {
                if (!bundle?.id) return acc;
                acc[String(bundle.id)] = bundle?.nom || bundle.id;
                return acc;
            }, {});
            setBundleNames(names);
        });
    }, []);

    useEffect(() => {
        if (!selectedEvent || !showPrivateLabels) {
            setProvaLabels({});
            return;
        }

        fetchAPI(`/prova_labels/${selectedEvent}`, data => setProvaLabels(data || {}), false);
    }, [selectedEvent, showPrivateLabels]);

    const provaOptions = useMemo(() => {
        if (!Array.isArray(props.horesProves)) return [];

        const seen = new Set();
        const publicProves = proves?.public || [];
        const privateProves = proves?.private || [];
        const adminProves = proves?.admin || [];
        const canSeeAdminProves = isHeadTecnica(userInfo);
        const canSeePrivateProves = isTecnica(userInfo);

        const isVisibleProva = (fullProvaName) => {
            if (adminProves.includes(fullProvaName)) return canSeeAdminProves;
            if (privateProves.includes(fullProvaName)) return canSeeAdminProves || canSeePrivateProves;
            if (publicProves.includes(fullProvaName)) return true;
            return false;
        };

        const options = props.horesProves.reduce((acc, entry) => {
            const provaId = entry?.prova;
            if (!provaId) return acc;

            const [castell, versio] = provaId.split('.');
            if (!castell || !versio) return acc;

            const value = `${castell}.${versio}`;
            if (seen.has(value)) return acc;

            const fullProvaName = `${castell}.${versio}.canvis`;
            if (!isVisibleProva(fullProvaName)) return acc;

            seen.add(value);

            const bundleName = bundleNames?.[castell] || castell;
            const privateLabel = showPrivateLabels ? provaLabels?.[provaId] : '';
            const labelSuffix = privateLabel ? ` - ${privateLabel}` : '';
            const baseLabel = `${bundleName}${labelSuffix}`;

            const startMoment = moment(entry.start);
            const endMoment = moment(entry.end);
            const hasStart = startMoment.isValid();
            const hasEnd = endMoment.isValid();
            const timeLabel = hasStart && hasEnd
                ? `${startMoment.format('HH:mm')} – ${endMoment.format('HH:mm')}`
                : hasStart
                    ? startMoment.format('HH:mm')
                    : null;

            const label = timeLabel ? `${timeLabel} · ${baseLabel}` : baseLabel;

            acc.push({
                value,
                castell,
                versio,
                label,
            });

            return acc;
        }, []);

        const hasSelection = selectedBundle && selectedVersio;
        const normalizedBundle = hasSelection ? String(selectedBundle) : null;
        const normalizedVersio = hasSelection ? String(selectedVersio) : null;
        const currentValue = hasSelection ? `${normalizedBundle}.${normalizedVersio}` : null;
        const currentFullProvaName = currentValue ? `${currentValue}.canvis` : null;

        if (
            currentValue &&
            currentFullProvaName &&
            isVisibleProva(currentFullProvaName) &&
            !options.some(option => option.value === currentValue)
        ) {
            const fallbackName = bundleNames?.[normalizedBundle] || normalizedBundle;
            const fallbackLabelEntry = Object.entries(showPrivateLabels ? provaLabels || {} : {})
                .find(([provaId]) => {
                    const [bundleId, versioId] = provaId.split('.');
                    return bundleId === normalizedBundle && versioId === normalizedVersio;
                });

            const fallbackLabel = fallbackLabelEntry
                ? `${fallbackName} - ${fallbackLabelEntry[1]}`
                : fallbackName;

            options.unshift({
                value: currentValue,
                castell: normalizedBundle,
                versio: normalizedVersio,
                label: fallbackLabel,
            });
        }

        return options;
    }, [props.horesProves, bundleNames, provaLabels, showPrivateLabels, selectedBundle, selectedVersio, proves, userInfo]);

    const selectedProvaValue = useMemo(() => {
        if (!selectedBundle || !selectedVersio) return '';
        return `${String(selectedBundle)}.${String(selectedVersio)}`;
    }, [selectedBundle, selectedVersio]);

    const selectedProvaLabel = useMemo(() => {
        if (!selectedProvaValue) return '';
        const match = provaOptions.find(option => option.value === selectedProvaValue);
        return match?.label || '';
    }, [provaOptions, selectedProvaValue]);

    const canShowProvaSelector = Boolean(
        selectedEvent &&
        provaOptions.length > 0 &&
        readonly === false
    );

    const handleProvaChange = (event) => {
        const { value } = event.target;
        if (!value || value === selectedProvaValue) return;

        const [castell, versio] = value.split('.');
        if (!castell || !versio) return;

        setInitializationOpacity && setInitializationOpacity(0);
        setSelectedBundle && setSelectedBundle(castell);
        setSelectedVersio && setSelectedVersio(versio);

        if (selectedEvent) {
            window.history.pushState(null, '', `/${selectedEvent}/${castell}/${versio}/edit`);
        }
    };

    useEffect(() => {
        if (readonly || !selectedEvent || !selectedBundle || !selectedVersio) {
            setWatcherInfo(previous => previous.length === 0 ? previous : []);
            return;
        }

        let cancelled = false;

        // Reset watcher data immediately when the selected prova changes so we don't
        // keep stale counts while the fresh data is being fetched.
        setWatcherInfo(previous => previous.length === 0 ? previous : []);

        const refreshWatcherInfo = () => {
            fetchAPI(`/watcher_info/${selectedEvent}/${selectedBundle}/${selectedVersio}`, data => {
                if (cancelled || !data) return;

                const info = Array.isArray(data.watcherInfo) ? data.watcherInfo : [];
                setWatcherInfo(previous => areWatcherInfoEqual(previous, info) ? previous : info);
            }, false);
        };

        refreshWatcherInfo();
        const intervalId = setInterval(refreshWatcherInfo, 5000);

        return () => {
            cancelled = true;
            clearInterval(intervalId);
        };
    }, [readonly, selectedEvent, selectedBundle, selectedVersio]);

    useEffect(() => {
        if (!socket) return;

        const handleAjuntamentRotated = (val) => setAjuntament(val);

        socket.on('.ajuntament_rotated', handleAjuntamentRotated);

        return () => {
            socket.off('.ajuntament_rotated', handleAjuntamentRotated);
        };
    }, [socket]);

    const [lastTimeout, setLastTimeout] = useState(null)

    useEffect(() => {
        if (!readonly) {
            clearTimeout(lastTimeout)
            const timeoutid = setTimeout(() => setHideAjuntament(true), 2000)
            setLastTimeout(timeoutid)
        }
    }, [hideAjuntament])

    return (<>
        { !readonly ? (
            <WatchersModal
                closed={closedWatcherPopup}
                setClosed={setClosedWatcherPopup}
                socket={socket}
                userDevices={userDevices}
                {...props}
            />
        ) : null }
        <div className="pissarra-aids-container" style={{ zIndex: 99999 }}>
            { canShowProvaSelector ? (
                <div className={`prova-selector ${isMobile ? 'mobile' : ''}`}>
                    <div className="prova-selector-panel">
                        <select
                            id="prova-selector-select"
                            value={selectedProvaValue}
                            onChange={handleProvaChange}
                            aria-label="Selecciona la prova"
                        >
                            {provaOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            ) : null }
            { ((readonly || !hideAjuntament) && ajuntament !== null && ajuntamentModalClosed !== false) && <div onClick={() => setHideAjuntament(true)} className={`ajuntament ${position} ${extended ? 'extended' : ''}`}>
                {referenceEmoji ? <span className="ajuntament-marker-icon">{referenceEmoji}</span> : null}
                <span className="ajuntament-marker-text">{referenceLabel}</span>
            </div> }

            { !readonly && <div className="watcherCount">
                <Pressable
                    onClick={() => setClosedWatcherPopup(false)}
                    style={{
                        padding: 10,
                        display: 'flex',
                        justifyContent: 'space-around',
                        alignItems: 'center',
                        backgroundColor: 'transparent',
                        borderRadius: 10,
                    }}
                >
                    <WatchersPreview
                        userDevices={userDevices}
                        {...props}
                    />
                </Pressable>
            </div> }

            { !readonly && isDesktop && <div className="placedCount">👤 {placedCount}</div> }

            {
                !readonly && isDesktop && <ChatButton {...props} castellersInfo={castellersInfo} />
            }

            {
                isDesktop && previewUser && castellersInfo?.[previewUser] && (
                    <div
                        className={`selected-user-preview ${isCopilotPreview ? 'copilot' : ''}`}
                        style={{
                            position: 'absolute',
                            top: 75,
                            left: 15,
                        }}
                    >
                        <ProfilePic
                            user={previewUser}
                            width={150}
                            height={150}
                            rounded={false}
                            touchToEnlarge={true}
                        />
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: 6,
                                margin: 10,
                            }}
                        >
                            <div
                                style={{
                                    backgroundColor: '#eee',
                                    padding: 5,
                                    paddingLeft: 10,
                                    paddingRight: 10,
                                    borderRadius: 5,
                                }}
                            >
                                {previewUserName}
                            </div>
                            {castellersInfo?.[previewUser]?.altura != null && <div
                                style={{
                                    backgroundColor: '#122C34',
                                    padding: 5,
                                    paddingLeft: 8,
                                    paddingRight: 8,
                                    borderRadius: 5,
                                    fontSize: 13,
                                    color: 'white',
                                }}
                            >
                                {castellersInfo[previewUser].altura}
                            </div>}
                        </div>
                        {
                            isCopilotPreview && (
                                <div className="copilot-preview-actions">
                                    <button
                                        className="copilot-btn copilot-btn-accept"
                                        onClick={() => acceptCopilotSuggestion('desktop_button')}
                                    >
                                        Acceptar
                                        <span className="copilot-tab-key">ENTER</span>
                                    </button>
                                    <button
                                        className="copilot-btn copilot-btn-ignore"
                                        onClick={() => dismissCopilotSuggestion('desktop_button')}
                                    >
                                        Ignorar
                                    </button>
                                </div>
                            )
                        }
                    </div>
                )
            }

            {
                isMobile &&
                <Pressable
                    style={{
                        position: 'absolute',
                        top: 15,
                        left: 15,
                        fontSize: 24,
                        padding: 10,
                        backgroundColor: '#eee',
                        borderRadius: 10,
                    }}
                    onClick={() => window.location.assign('/')}
                >
                    ⬅️
                </Pressable>
            }
        </div>
    </>)
}

export default PissarraAids;
